import { useState, useEffect } from "react";
import { LegacyVoiceOption as VoiceOption } from "@/constants/audioVoice";
import { FaPause, FaPlay } from "react-icons/fa";
import { Check } from "lucide-react";
import ProgressBar from "../ProgressBar";
import Dropdown from "../Dropdown";

const DefaultVoice = ({
  voice,
  audioRef,
  isPlaying,
  currentSpeed,
  handleSpeedChange,
  togglePlayPause,
  progress = 0,
  speedOptions: overrideSpeedOptions,
  stackSpeed = false,
}: {
  voice: VoiceOption;
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  currentSpeed: string;
  handleSpeedChange: (speed: string) => void;
  togglePlayPause: () => void;
  progress?: number;
  speedOptions?: VoiceOption["speedOptions"];
  stackSpeed?: boolean;
}) => {
  const [isSpeedDropdownOpen, setIsSpeedDropdownOpen] = useState(false);
  const speedOptions = overrideSpeedOptions ?? voice.speedOptions;
  const currentSpeedLabel =
    speedOptions.find((opt) => opt.value === currentSpeed)?.label ||
    currentSpeed;
  const handleSpeedSelect = (speed: string) => {
    handleSpeedChange(speed);
    setIsSpeedDropdownOpen(false);
  };
  useEffect(() => {
    if (currentSpeed && currentSpeed !== voice.defaultSpeed) {
      handleSpeedSelect(currentSpeed);
    }
  }, [currentSpeed]);

  if (stackSpeed) {
    return (
      <div className="flex flex-col flex-1 w-full justify-between rounded-xl shadow-[0px_0px_5px_0px_rgba(0,0,0,0.1)] p-3">
        <audio ref={audioRef} preload="auto" />
        {/* Top: avatar + name */}
        <div className="flex flex-row items-center gap-2">
          <img
            src={voice.icon}
            alt={voice.label}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
          <p className="text-sm font-bold text-[#252525] truncate">
            {voice.label}
          </p>
        </div>
        {/* Middle: speed */}
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-[#252525]">Speed:</p>
          <div className="w-full">
            <Dropdown
              title={currentSpeedLabel}
              isOpen={isSpeedDropdownOpen}
              onToggle={() => setIsSpeedDropdownOpen(!isSpeedDropdownOpen)}
              variant="primary"
              buttonClassName="!p-2 !text-sm"
            >
              {speedOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSpeedSelect(option.value)}
                  className="w-full text-left text-sm px-3 py-1 hover:bg-gray-100 transition-colors flex items-center justify-between text-[#252525]"
                >
                  <span>{option.label}</span>
                  {option.value === currentSpeed && (
                    <Check className="w-4 h-4 text-[#28543D]" />
                  )}
                </button>
              ))}
            </Dropdown>
          </div>
        </div>
        {/* Bottom: play button + progress bar */}
        <div className="flex flex-row items-center gap-2">
          <button
            onClick={togglePlayPause}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-[#28543D] hover:bg-[#204833] transition-colors flex-shrink-0"
          >
            {isPlaying ? (
              <FaPause className="w-3.5 h-3.5 text-white" />
            ) : (
              <FaPlay className="w-3.5 h-3.5 text-white" />
            )}
          </button>
          <div className="flex-1">
            <ProgressBar progress={progress} color="#FFD84D" height="6px" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full [container-type:inline-size]">
      <div className="flex flex-row justify-between items-center gap-2 px-3 py-2 border-t-2 border-[#28543D]/10">
        <div className="flex flex-row items-center gap-2">
          <audio ref={audioRef} preload="auto" />
          <button
            onClick={togglePlayPause}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-[#28543D] hover:bg-[#204833] transition-colors flex-shrink-0"
          >
            {isPlaying ? (
              <FaPause className="w-3.5 h-3.5 text-white" />
            ) : (
              <FaPlay className="w-3.5 h-3.5 text-white" />
            )}
          </button>
          <img
            src={voice.icon}
            alt={voice.label}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
          <p className="text-sm font-bold text-[#252525] truncate">
            {voice.label}
          </p>
        </div>
        <div className="flex flex-row items-center gap-2 flex-shrink-0">
          <p className="text-sm font-semibold text-[#252525] hidden [@container_(min-width:360px)]:block">
            Speed:
          </p>
          <div className="w-[120px]">
            <Dropdown
              title={currentSpeedLabel}
              isOpen={isSpeedDropdownOpen}
              onToggle={() => setIsSpeedDropdownOpen(!isSpeedDropdownOpen)}
              variant="primary"
              buttonClassName="!p-2 !text-sm"
            >
              {speedOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSpeedSelect(option.value)}
                  className="w-full text-left text-sm px-3 py-1 hover:bg-gray-100 transition-colors flex items-center justify-between text-[#252525]"
                >
                  <span>{option.label}</span>
                  {option.value === currentSpeed && (
                    <Check className="w-4 h-4 text-[#28543D]" />
                  )}
                </button>
              ))}
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="px-3">
        <ProgressBar progress={progress} color="#FFD84D" height="6px" />
      </div>
    </div>
  );
};

export default DefaultVoice;
